import React, { useMemo } from 'react';
import { FundOutlined } from '@ant-design/icons';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Line } from 'recharts';
import ChartEmptyState from './ChartEmptyState';
import { isAllZeroTimeSeries } from '../utils/dashboardEmptyState';
import {
  REQUESTS_CHART_SERIES,
  REQUESTS_TIME_SERIES,
  STACKED_AREA_STROKE,
  filterActiveSeries,
  getNonZeroSeriesEntriesAtPoint,
  getSeriesEntriesAtPoint,
  sumSeriesEntries,
} from '../utils/chartTimeSeries';

const StackedAreaChart = ({
  data,
  height = 300,
  emptyState = null,
  series = REQUESTS_CHART_SERIES,
  valueKeys = REQUESTS_TIME_SERIES.valueKeys,
  flatLineKey = 'successful',
  totalLabel = 'Total',
  formatValue = (value) => Number(value).toLocaleString(),
  yAxisMin = 4,
  onSeriesClick,
}) => {
  const chartData = data && data.length > 0 ? data : [];

  const activeSeries = useMemo(
    () => filterActiveSeries(chartData, series),
    [chartData, series]
  );

  const usesChannelBreakdown = activeSeries.length > 0;

  const renderedSeries = useMemo(() => {
    if (usesChannelBreakdown) {
      return activeSeries;
    }
    return [
      { key: 'failed', label: 'Failed', color: '#ef4444' },
      { key: 'successful', label: 'Successful', color: '#10b981' },
    ];
  }, [activeSeries, usesChannelBreakdown]);

  const isFlatZero = isAllZeroTimeSeries(chartData, valueKeys);
  const flatLineColor = '#cbd5e1';

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    if (usesChannelBreakdown) {
      const entriesAtPoint = getSeriesEntriesAtPoint(payload, activeSeries);
      const visibleEntries = getNonZeroSeriesEntriesAtPoint(payload, activeSeries);
      const total = sumSeriesEntries(entriesAtPoint);

      return (
        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '12px',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '8px', fontWeight: 500 }}>
            {`Time: ${label}`}
          </p>
          {visibleEntries.length > 0 ? visibleEntries.map((entry) => (
            <p
              key={entry.key}
              style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: entry.color, marginBottom: '4px' }}
            >
              {`${entry.label}: ${formatValue(entry.value)}`}
            </p>
          )) : (
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)' }}>
              No activity at this time
            </p>
          )}
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: 'var(--color-text-secondary)',
            borderTop: '1px solid var(--color-border-light)',
            paddingTop: '8px',
            marginTop: '8px',
            fontWeight: 500,
          }}>
            {`${totalLabel}: ${formatValue(total)}`}
          </p>
        </div>
      );
    }

    const successful = payload.find((entry) => entry.dataKey === 'successful')?.value || 0;
    const failed = payload.find((entry) => entry.dataKey === 'failed')?.value || 0;
    const total = Number(successful) + Number(failed);

    return (
      <div style={{
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '12px',
        boxShadow: 'var(--shadow-lg)',
      }}>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '8px', fontWeight: 500 }}>
          {`Time: ${label}`}
        </p>
        {total > 0 ? (
          <>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#10b981', marginBottom: '4px' }}>
              {`Successful: ${formatValue(successful)}`}
            </p>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#ef4444', marginBottom: '4px' }}>
              {`Failed: ${formatValue(failed)}`}
            </p>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            No activity at this time
          </p>
        )}
        <p style={{
          margin: 0,
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
          borderTop: '1px solid var(--color-border-light)',
          paddingTop: '8px',
          marginTop: '8px',
          fontWeight: 500,
        }}>
          {`${totalLabel}: ${total.toLocaleString()}`}
        </p>
      </div>
    );
  };

  const chartMargin = { top: 10, right: 10, left: 0, bottom: 10 };
  const xAxisProps = {
    dataKey: 'time',
    axisLine: false,
    tickLine: false,
    tick: { fontSize: 12, fill: 'var(--color-text-muted)' },
    padding: { left: 0, right: 0 },
    scale: 'point',
  };

  if (emptyState) {
    return (
      <ChartEmptyState
        icon={FundOutlined}
        title={emptyState.title}
        description={emptyState.description}
        actionLabel={emptyState.actionLabel}
        onAction={emptyState.onAction}
        tone={emptyState.tone}
        height={height}
      />
    );
  }

  const renderStackedAreas = (areaSeries) =>
    areaSeries.map(({ key, color }) => (
      <Area
        key={key}
        type="monotone"
        dataKey={key}
        stackId="1"
        stroke={STACKED_AREA_STROKE}
        strokeWidth={0}
        fill={`url(#gradient-${key})`}
        isAnimationActive={false}
        style={onSeriesClick ? { cursor: 'pointer' } : undefined}
        onClick={onSeriesClick ? () => onSeriesClick(key) : undefined}
      />
    ));

  return (
    <div style={{ width: '100%', height: height, minHeight: height, display: 'flex', flexDirection: 'column' }}>
      <ResponsiveContainer width="100%" height="100%" style={{ flex: 1 }}>
        <AreaChart data={chartData} margin={chartMargin}>
          <defs>
            {renderedSeries.map(({ key, color }) => (
              <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={color} stopOpacity={0.15} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" vertical={false} />
          <XAxis {...xAxisProps} />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }}
            tickFormatter={(value) => formatValue(value)}
            domain={[0, (max) => Math.max(max, yAxisMin)]}
            allowDataOverflow
          />
          <Tooltip content={customTooltip} />
          {isFlatZero ? (
            <Line
              type="monotone"
              dataKey={flatLineKey}
              stroke={flatLineColor}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: flatLineColor, strokeWidth: 0 }}
            />
          ) : (
            renderStackedAreas(renderedSeries)
          )}
        </AreaChart>
      </ResponsiveContainer>

      {!isFlatZero && usesChannelBreakdown ? (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '24px',
          paddingTop: '16px',
          width: '100%',
          flexWrap: 'wrap',
        }}>
          {activeSeries.map(({ key, label, color }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                background: color,
                flexShrink: 0,
              }} />
              <span style={{ fontSize: '12.5px', fontWeight: '500', color: 'var(--color-text)' }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default StackedAreaChart;
